import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Gift, Trash2, Tablet, UserCheck, Store } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const CustomerRewardsReport = () => {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRewards = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/customer-rewards`, { params: { q } });
      setData(res);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(fetchRewards, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Trash-bin handler — admin confirms before nuking a customer's rewards.
  const handleClearRewards = async (customer) => {
    const ok = await confirmDialog(
      `هل تريد تأكيد حذف أرباح الزبون "${customer.full_name || customer.customer_name}"؟\nالمبلغ الحالي: ${fmt(customer.rewards_balance)} د.ع\nهذا الإجراء نهائي.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/customer-rewards/${customer.id}`);
      toast.success(`تم حذف أرباح الزبون (${fmt(customer.rewards_balance)} د.ع)`);
      fetchRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-rewards-report">
      <div className="bg-white border-2 border-[#E85D45] rounded-2xl p-4">
        <div className="relative">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C6E64]" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث في الزبائن لرؤية أرباحهم..."
            data-testid="rewards-search"
            className="w-full pr-12 pl-4 py-3 rounded-2xl border border-[#E4EBE6] bg-white focus:outline-none focus:ring-2 focus:ring-[#E85D45] text-lg" />
        </div>
      </div>

      {data && (
        <div className="bg-gradient-to-l from-[#F5A623] to-[#E68A0A] text-white rounded-2xl p-4 grid grid-cols-2 gap-2 text-center">
          <div><p className="text-xs opacity-90">عدد الزبائن</p><p className="font-bold text-2xl">{fmt(data.count)}</p></div>
          <div><p className="text-xs opacity-90">إجمالي الأرباح</p><p className="font-bold text-2xl" dir="ltr">{fmt(data.total_rewards)} د.ع</p></div>
        </div>
      )}

      {loading ? <p className="text-center text-[#5C6E64]">جاري التحميل...</p> : (
        data && data.customers.length === 0 ? (
          <p className="text-center text-[#5C6E64] py-8">لا توجد أرباح لعرضها</p>
        ) : (
          <div className="space-y-2">
            {data?.customers.map((u) => {
              const isRegular = u.customer_type === "regular";
              return (
                <div key={u.id} data-testid={`rewards-row-${u.id}`}
                  className="bg-white border border-[#E4EBE6] rounded-2xl p-3 flex justify-between items-center gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Gift className="w-5 h-5 text-[#F5A623] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-bold text-[#111814] truncate">
                          {u.full_name || u.customer_name}
                        </p>
                        {/* iPad/Regular badge next to the name */}
                        <span
                          className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[9px] font-black text-white shrink-0`}
                          style={{
                            background: isRegular
                              ? "linear-gradient(135deg, #1E3A5F 0%, #1D4ED8 100%)"
                              : "linear-gradient(135deg, #15803D 0%, #22C55E 100%)",
                          }}
                          data-testid={`rewards-type-badge-${u.id}`}
                        >
                          {isRegular ? <UserCheck className="w-2.5 h-2.5" /> : <Tablet className="w-2.5 h-2.5" />}
                          {isRegular ? "زبون" : "ايباد"}
                        </span>
                        {/* Tier badge */}
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-black text-white shrink-0"
                          style={{
                            background: u.customer_tier === "B_plus"
                              ? "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)"
                              : "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
                          }}
                        >
                          {u.customer_tier === "B_plus" ? "B+" : "A+"}
                        </span>
                      </div>
                      {u.market_name && (
                        <p className="text-[11px] text-[#5C6E64] flex items-center gap-1">
                          <Store className="w-3 h-3" />
                          {u.market_name}
                        </p>
                      )}
                      {u.phone && (
                        <a href={`tel:${u.phone}`} className="text-xs text-[#5C6E64]" dir="ltr">{u.phone}</a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="font-bold text-lg text-[#F5A623]" dir="ltr">{fmt(u.rewards_balance)} د.ع</p>
                    <button
                      type="button"
                      onClick={() => handleClearRewards(u)}
                      data-testid={`rewards-trash-${u.id}`}
                      title="حذف أرباح هذا الزبون"
                      className="w-9 h-9 rounded-full bg-[#E85D45] hover:bg-[#cf4933] text-white flex items-center justify-center transition-colors shadow-sm active:scale-95"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default CustomerRewardsReport;
